import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useState,
} from "react";

import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import { ZodSchema, z } from "zod";

import { ObjectKeys } from "@/utils/object.utils";
import { parseUrlSearchParamsToObject } from "@/utils/urlSearchParams.utils";

import { getUrlSearchParams } from "./searchParams.helpers";

type SearchParamsContextType<Schema extends ZodSchema> = ReturnType<
  typeof useCreateSearchParamsContextValue<Schema>
>;

let SearchParamsContext: ReturnType<
  typeof createContext<SearchParamsContextType<ZodSchema> | null>
> | null = null;

/** @description return the context using singleton pattern to satisfy the generic type */
function getTypedSearchParamsContext<Schema extends ZodSchema>() {
  if (!SearchParamsContext) {
    SearchParamsContext = createContext<SearchParamsContextType<Schema> | null>(
      null,
    );
  }

  return SearchParamsContext as ReturnType<
    typeof createContext<SearchParamsContextType<Schema>>
  >;
}

// because the useSearchParams hook is not consistent and loosing its value after a few calls
// also doesn't track between shallow routing
const useClientUrlSearchParams = () => {
  const router = useRouter();
  const urlSearchParams = React.useMemo(() => {
    return router.asPath.includes("?")
      ? new URLSearchParams(router.asPath.split("?")[1])
      : new URLSearchParams();
  }, [router.asPath]);

  return urlSearchParams;
};

const useCreateSearchParamsContextValue = <Schema extends ZodSchema>(
  defaultValue: URLSearchParams | null = null,
  schema: Schema,
) => {
  const pathName = usePathname();
  const router = useRouter();

  const isUrlParamsSetOnceFromAnySource = React.useRef(false);

  const [urlSearchParamsState, rawSetUrlSearchParamsState] =
    useState<z.infer<Schema> | null>(null);
  const setUrlSearchParamsState = useCallback<
    React.Dispatch<React.SetStateAction<z.infer<Schema>>>
  >(
    (newState: z.infer<Schema>) => {
      rawSetUrlSearchParamsState(newState);

      if (!!newState) {
        isUrlParamsSetOnceFromAnySource.current = true;
      }
    },
    [rawSetUrlSearchParamsState],
  );

  // setting defualt search params from server (from props) //
  useEffect(() => {
    if (!defaultValue) return;
    const queryParamsAsObject = parseUrlSearchParamsToObject(defaultValue);

    const parsedConvertedQueryParams = schema.safeParse(queryParamsAsObject);

    if (
      parsedConvertedQueryParams.success &&
      ObjectKeys(parsedConvertedQueryParams.data).length > 0
    ) {
      setUrlSearchParamsState(parsedConvertedQueryParams.data);
    }
  }, [defaultValue, schema, setUrlSearchParamsState]);
  // //

  // setting default search params from client (from url) //
  const defaultClientURLSearchParams = useClientUrlSearchParams();

  useEffect(() => {
    const queryParamsAsObject = defaultClientURLSearchParams
      ? parseUrlSearchParamsToObject(defaultClientURLSearchParams)
      : null;

    const parsedConvertedQueryParams = schema.safeParse(queryParamsAsObject);

    if (parsedConvertedQueryParams.success) {
      setUrlSearchParamsState(parsedConvertedQueryParams.data);
    }
  }, [schema, defaultClientURLSearchParams, setUrlSearchParamsState]);
  // //

  const pushToRouter = useCallback(
    (query: z.infer<Schema>) => {
      router.push(pathName, { query });
    },
    [pathName, router],
  );

  const setUrlSearchParams = useCallback(
    (queryParams: z.infer<Schema>, { append }: { append?: boolean } = {}) => {
      setUrlSearchParamsState((value) => {
        if (append) {
          const query = getUrlSearchParams(
            { ...value, ...queryParams },
            schema,
          );

          pushToRouter(query);
          return query;
        }

        const query = getUrlSearchParams(queryParams, schema);

        pushToRouter(query);
        return query;
      });
    },
    [pushToRouter, schema, setUrlSearchParamsState],
  );

  return {
    urlSearchParams: urlSearchParamsState,
    setUrlSearchParams,
    rawSSRUrlSearchParams: defaultValue,
  };
};

type PropTypes<Schema extends ZodSchema> = PropsWithChildren & {
  defaultValue: URLSearchParams;
  schema: Schema;
};

export const SearchParamsContextProviderV2 = <Schema extends ZodSchema>({
  children,
  defaultValue,
  schema,
}: PropTypes<Schema>): React.ReactElement => {
  const value = useCreateSearchParamsContextValue<Schema>(defaultValue, schema);
  const SearchParamsContext = getTypedSearchParamsContext<Schema>();

  return (
    <SearchParamsContext.Provider value={value}>
      {children}
    </SearchParamsContext.Provider>
  );
};

export const useSearchParamsContextV2 = <Schema extends ZodSchema>(
  _schema: Schema,
) => {
  const context = getTypedSearchParamsContext<Schema>();
  const value = React.useContext(context);

  if (!value) {
    throw new Error(
      "useSearchParamsContextV2 must be used within a SearchParamsContextProviderV2",
    );
  }

  return value;
};
